import React, { useMemo, useRef, useState } from 'react';
import { useApolloClient, useMutation, useQuery } from '@apollo/client';
import {
  Box, Typography, Paper, Stack, Autocomplete, TextField, Button, Alert, Chip, LinearProgress, Table, TableHead,
  TableRow, TableCell, FormControlLabel, Checkbox, RadioGroup, Radio, Divider, Grid,
} from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import DownloadIcon from '@mui/icons-material/Download';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import TableBody from '../components/PagedTableBody';
import { BULK_IMPORT_MODULES_QUERY, BULK_IMPORT_TEMPLATE_QUERY, BULK_EXPORT_CSV_QUERY, BULK_IMPORT_MUTATION } from '../graphql/queries';

const saveCsv = (name, text) => {
  const url = URL.createObjectURL(new Blob(['﻿', text], { type: 'text/csv;charset=utf-8' }));
  const a = document.createElement('a');
  a.href = url; a.download = name; a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};

// Admin > Bulk Data Load: pick a module, download its CSV template (every field of the module),
// fill it in, upload it, check it, and load it. Rows that already exist are skipped or updated.
export default function AdminBulkImportPage() {
  const client = useApolloClient();
  const fileRef = useRef(null);
  const { data, loading } = useQuery(BULK_IMPORT_MODULES_QUERY);
  const [run, { loading: running }] = useMutation(BULK_IMPORT_MUTATION);
  const [moduleKey, setModuleKey] = useState(null);
  const [file, setFile] = useState(null); // { name, text, rows }
  const [existing, setExisting] = useState('skip');
  const [onlyValid, setOnlyValid] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState('');

  const modules = useMemo(() => data?.bulkImportModules || [], [data]);
  const mod = modules.find(m => m.key === moduleKey) || null;

  const download = async (kind) => {
    setError(''); setBusy(kind);
    try {
      const { data: d } = await client.query({
        query: kind === 'template' ? BULK_IMPORT_TEMPLATE_QUERY : BULK_EXPORT_CSV_QUERY,
        variables: { module: mod.key }, fetchPolicy: 'network-only',
      });
      saveCsv(`${mod.key}${kind === 'template' ? '_template' : '_data'}.csv`, kind === 'template' ? d.bulkImportTemplate : d.bulkExportCsv);
    } catch (e) { setError(e.message); }
    setBusy('');
  };

  const pickFile = (e) => {
    const f = e.target.files?.[0];
    e.target.value = '';
    if (!f) return;
    if (f.size > 10 * 1024 * 1024) { setError('The file is larger than 10 MB. Split it into smaller files.'); return; }
    const reader = new FileReader();
    reader.onload = () => {
      const text = String(reader.result || '');
      setFile({ name: f.name, text, rows: Math.max(0, text.split(/\r\n|\n|\r/).filter(l => l.trim()).length - 1) });
      setResult(null); setError('');
    };
    reader.readAsText(f);
  };

  const go = async (mode) => {
    setError('');
    try {
      const { data: d } = await run({ variables: { module: mod.key, csv: file.text, mode, existing, onlyValid } });
      setResult({ ...d.bulkImport, mode });
    } catch (e) { setError(e.message); }
  };

  const imported = result?.mode === 'import' && (result.created || result.updated);
  const canImport = file && result?.mode === 'validate' && result.valid > 0 && (!result.failed || onlyValid);

  return (
    <Box>
      <Box mb={3}>
        <Typography variant="h4" fontWeight={700}>Bulk Data Load</Typography>
        <Typography variant="body2" color="text.secondary">
          Load any module's data from a CSV file. Download the module's template (every field, with an example row), fill it in with
          Excel or Google Sheets, save as CSV, upload it, check it, then load it. Links to other records use readable values: a user's
          email, a ceremony's name, a caterer's name and so on.
        </Typography>
      </Box>
      {loading && <LinearProgress sx={{ mb: 2 }} />}

      {/* 1. Module */}
      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Typography variant="subtitle1" fontWeight={700} mb={1}>1. Choose the data to load</Typography>
        <Autocomplete
          options={modules}
          groupBy={m => (m.menu.startsWith('Admin') ? 'Catalogues & settings (admin)' : 'Application data')}
          getOptionLabel={m => `${m.label} — ${m.menu}`}
          value={mod}
          onChange={(_, v) => { setModuleKey(v?.key || null); setResult(null); setFile(null); }}
          renderInput={params => <TextField {...params} label={`Module (${modules.length})`} placeholder="e.g. Guests, Vendor offerings, Contract lines" />}
        />
        {mod && (
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} mt={2} alignItems={{ sm: 'center' }}>
            <Button variant="contained" startIcon={<DownloadIcon />} onClick={() => download('template')} disabled={Boolean(busy)}>
              Download template
            </Button>
            <Button variant="outlined" startIcon={<DownloadIcon />} onClick={() => download('export')} disabled={Boolean(busy)}>
              Download current data
            </Button>
            <Typography variant="caption" color="text.secondary">
              {mod.naturalKey.length
                ? <>Rows that already exist are recognised by <strong>{mod.naturalKey.join(' + ')}</strong>.</>
                : 'Every row is added as a new record.'}
            </Typography>
          </Stack>
        )}
      </Paper>

      {/* Field guide */}
      {mod && (
        <Paper variant="outlined" sx={{ mb: 2, overflowX: 'auto' }}>
          <Typography variant="subtitle1" fontWeight={700} sx={{ p: 2, pb: 1 }}>
            Columns for {mod.label} ({mod.columns.length}; {mod.columns.filter(c => c.required).length} required)
          </Typography>
          <Table size="small" sx={{ minWidth: 720 }}>
            <TableHead>
              <TableRow sx={{ '& th': { fontWeight: 700, bgcolor: 'grey.100' } }}>
                <TableCell>Column</TableCell><TableCell>Meaning</TableCell><TableCell>Format</TableCell><TableCell>Allowed values</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {mod.columns.map(c => (
                <TableRow key={c.header}>
                  <TableCell sx={{ fontFamily: 'monospace', whiteSpace: 'nowrap' }}>
                    {c.header}{c.required && <Chip size="small" color="error" label="required" sx={{ ml: 1, height: 18 }} />}
                  </TableCell>
                  <TableCell>{c.label}</TableCell>
                  <TableCell>{c.hint}</TableCell>
                  <TableCell sx={{ maxWidth: 280 }}>
                    <Typography variant="caption">{(c.options || []).join(', ')}</Typography>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      )}

      {/* 2. File */}
      {mod && (
        <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
          <Typography variant="subtitle1" fontWeight={700} mb={1}>2. Upload the CSV file</Typography>
          <input ref={fileRef} type="file" accept=".csv,text/csv" hidden onChange={pickFile} />
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
            <Button variant="outlined" startIcon={<UploadFileIcon />} onClick={() => fileRef.current?.click()}>Choose CSV file</Button>
            {file && <Typography variant="body2"><strong>{file.name}</strong> · {file.rows} data row(s)</Typography>}
          </Stack>
          {file && (
            <Grid container spacing={2} mt={0.5}>
              <Grid item xs={12} md={6}>
                <Typography variant="caption" fontWeight={700} color="text.secondary">If a row already exists</Typography>
                <RadioGroup row value={existing} onChange={e => { setExisting(e.target.value); setResult(null); }}>
                  <FormControlLabel value="skip" control={<Radio size="small" />} label="Skip it" />
                  <FormControlLabel value="update" control={<Radio size="small" />} label="Update it with the file's values" />
                </RadioGroup>
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControlLabel
                  control={<Checkbox checked={onlyValid} onChange={e => setOnlyValid(e.target.checked)} />}
                  label="Load the valid rows even if some rows have errors"
                />
              </Grid>
            </Grid>
          )}
        </Paper>
      )}

      {/* 3. Check & load */}
      {mod && file && (
        <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
          <Typography variant="subtitle1" fontWeight={700} mb={1}>3. Check and load</Typography>
          <Stack direction="row" spacing={1.5}>
            <Button variant="outlined" startIcon={<FactCheckIcon />} onClick={() => go('validate')} disabled={running}>Check file</Button>
            <Button variant="contained" startIcon={<CloudUploadIcon />} onClick={() => go('import')} disabled={running || !canImport}>
              Load {result?.mode === 'validate' ? `${result.valid} row(s)` : 'data'}
            </Button>
          </Stack>
          {!result && <Typography variant="caption" color="text.secondary" display="block" mt={1}>Check the file first; loading is enabled once it has valid rows.</Typography>}
          {running && <LinearProgress sx={{ mt: 2 }} />}
        </Paper>
      )}

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      {result && (
        <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap mb={1.5}>
            <Chip label={`${result.total} row(s)`} />
            <Chip color="success" variant="outlined" label={`${result.valid} valid`} />
            {result.failed > 0 && <Chip color="error" label={`${result.failed} with errors`} />}
            {result.mode === 'import' && <>
              <Chip color="success" label={`${result.created} added`} />
              <Chip color="primary" label={`${result.updated} updated`} />
              <Chip label={`${result.skipped} already there (skipped)`} />
            </>}
          </Stack>
          {result.warnings?.map(w => <Alert key={w} severity="warning" sx={{ mb: 1 }}>{w}</Alert>)}
          {result.mode === 'validate' && !result.failed && <Alert severity="success">The file is valid. Click "Load" to load it.</Alert>}
          {result.mode === 'validate' && result.failed > 0 && (
            <Alert severity="warning">
              Fix the rows below and upload the file again{result.valid ? ', or tick "Load the valid rows even if some rows have errors"' : ''}.
            </Alert>
          )}
          {result.mode === 'import' && imported && <Alert severity="success">Data loaded.</Alert>}
          {result.mode === 'import' && !imported && !result.errors.length && <Alert severity="info">Nothing new to load: every row was already there.</Alert>}
          {result.errors.length > 0 && (
            <>
              <Divider sx={{ my: 1.5 }} />
              <Typography variant="subtitle2" fontWeight={700} mb={1}>Problems ({result.errors.length})</Typography>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ '& th': { fontWeight: 700, bgcolor: 'grey.100' } }}>
                    <TableCell>Row</TableCell><TableCell>Column</TableCell><TableCell>Problem</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {result.errors.map((e, i) => (
                    <TableRow key={i}>
                      <TableCell>{e.row || '—'}</TableCell>
                      <TableCell sx={{ fontFamily: 'monospace' }}>{e.column}</TableCell>
                      <TableCell>{e.message}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </>
          )}
        </Paper>
      )}
    </Box>
  );
}
